<template>
  <a
    v-if="href"
    :href="href"
    target="_blank"
    class="button is-fullwidth is-spaced is-external"
    v-bind="attributes"
  >
    <div v-bind="innerAttributes">
      <span>{{ $texts('reviews', 'Read reviews') }}</span>
      <template v-if="ratingText">
        <span
          itemprop="itemReviewed"
          itemscope
          itemtype="https://schema.org/Organization"
          class="is-sr-only"
        >
          <meta itemprop="name" :content="itemReviewedName" />
        </span>
        (<span itemprop="ratingValue">{{ ratingText }}</span
        >/<span itemprop="bestRating">{{ ratingTotalText }}</span
        >)
        <meta
          v-if="ratingCount"
          itemprop="ratingCount"
          :content="ratingCount.toString()"
        />
      </template>
    </div>
    <SpriteSymbol name="chevron-right" />
  </a>
</template>

<script lang="ts" setup>
import type { StorageStaticUrlFragment } from '#graphql-operations'

/* Migration of:
import { PropOptions } from 'vue/types'
import Vue from 'vue'
import { StorageStaticUrlFragment } from '~/types/graphql-operations'
export default Vue.extend({
 name: 'DetailReview',
 props: {
   url: {
     type: Object,
     default: null,
   } as PropOptions<StorageStaticUrlFragment['url']>,
   itemReviewedName: {
     type: String,
     default: '',
   },
   rating: {
     type: Number,
     default: 0,
   },
   ratingTotal: {
     type: Number,
     default: 5,
   },
   ratingCount: {
     type: Number,
     default: 0,
   },
 },
*/

const props = withDefaults(
  defineProps<{
    url?: StorageStaticUrlFragment['url']
    itemReviewedName?: string
    rating?: number
    ratingTotal?: number
    ratingCount?: number
  }>(),
  {
    url: undefined,
    itemReviewedName: '',
    rating: 0,
    ratingTotal: 5,
    ratingCount: 0,
  },
)

const { $texts } = useEasyTexts()

/* Migration of:
computed: {
 href(): string | undefined {
   return this.url?.url?.path
 },
*/
const href = computed(() => props.url?.uri?.path)

/* Migration of:
ratingText(): string {
 if (!this.rating) {
   return ''
 }
 return this.rating.toFixed(1)
},
*/
const ratingText = computed(() =>
  !props.rating ? '' : props.rating.toFixed(1),
)

/* Migration of:
ratingTotalText(): string {
 return (this.ratingTotal || 5).toFixed(1)
},
*/
const ratingTotalText = computed(() => (props.ratingTotal || 5).toFixed(1))

/* Migration of:
attributes(): Record<string, any> {
 return this.ratingText
   ? {
       itemscope: true,
       itemtype: 'https://schema.org/Organization',
     }
   : {}
},
*/
const attributes = computed(() =>
  ratingText.value
    ? {
        itemscope: true,
        itemtype: 'https://schema.org/Organization',
      }
    : {},
)

/* Migration of:
innerAttributes(): Record<string, any> {
 return this.ratingText
   ? {
       itemprop: 'aggregateRating',
       itemscope: true,
       itemtype: 'https://schema.org/AggregateRating',
     }
   : {}
},
*/
const innerAttributes = computed(() =>
  ratingText.value
    ? {
        itemprop: 'aggregateRating',
        itemscope: true,
        itemtype: 'https://schema.org/AggregateRating',
      }
    : {},
)
</script>
